import { ChangeDetectionStrategy, Component, Input, signal, WritableSignal, inject, OnInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { WizardItem } from '../../features/platform/services/ui-config-api.service';
import { AuthService } from '../../core/services/auth.service';
import { ThemeService } from '../../core/services/theme.service';
import { CommandPaletteComponent } from '../../core/components/command-palette.component';

@Component({
  standalone: true,
  selector: 'app-dynamic-wizard',
  imports: [CommonModule, RouterLink, RouterLinkActive],
  template: `
    <ng-container *ngFor="let item of filteredWizardItems(); let i = index">
      <button
        *ngIf="(item.action === 'route' || !item.action) && item.route"
        [routerLink]="item.route"
        routerLinkActive="bg-purple-100 dark:bg-purple-900/30"
        class="relative p-3 rounded-full bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 transition-all hover:scale-110 shadow-md glass-icon"
        [attr.title]="item.tooltip || item.label">
        <svg class="h-5 w-5 text-slate-700 dark:text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path [attr.d]="item.icon" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" />
        </svg>
        <span *ngIf="item.badge" 
              class="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white dark:border-slate-900">
          {{ item.badge }}
        </span>
      </button>

      <button
        *ngIf="item.action === 'command'"
        (click)="handleCommand(item.command!)"
        class="relative p-3 rounded-full bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 transition-all hover:scale-110 shadow-md glass-icon"
        [attr.title]="item.tooltip || item.label">
        <svg class="h-5 w-5 text-slate-700 dark:text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path [attr.d]="item.icon" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" />
        </svg>
        <span *ngIf="item.badge" 
              class="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white dark:border-slate-900">
          {{ item.badge }}
        </span>
      </button>

      <button
        *ngIf="item.action === 'modal'"
        (click)="openModal(item.modal!)"
        class="relative p-3 rounded-full bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 transition-all hover:scale-110 shadow-md glass-icon"
        [attr.title]="item.tooltip || item.label">
        <svg class="h-5 w-5 text-slate-700 dark:text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path [attr.d]="item.icon" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" />
        </svg>
        <span *ngIf="item.badge" 
              class="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white dark:border-slate-900">
          {{ item.badge }}
        </span>
      </button>

      <a
        *ngIf="item.action === 'external' && item.externalUrl"
        [href]="item.externalUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="relative p-3 rounded-full bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 transition-all hover:scale-110 shadow-md glass-icon"
        [attr.title]="item.tooltip || item.label">
        <svg class="h-5 w-5 text-slate-700 dark:text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path [attr.d]="item.icon" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" />
        </svg>
        <span *ngIf="item.badge" 
              class="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white dark:border-slate-900">
          {{ item.badge }}
        </span>
      </a>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DynamicWizardComponent implements OnInit, OnChanges {
  @Input() wizardItems: WizardItem[] = [];
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly theme = inject(ThemeService);

  filteredWizardItems = signal<WizardItem[]>([]);

  ngOnChanges(): void {
    this.filterWizardItems();
  }

  ngOnInit(): void {
    this.filterWizardItems();
  }

  private filterWizardItems(): void {
    const userRoles = this.auth.roles();
    const isPlatformOwner = this.auth.isPlatformOwner() || 
                           userRoles.some(r => {
                             const name = r.toLowerCase();
                             return name === 'platform_owner' || name === 'platform owner' || name === 'super_admin' || name === 'super admin';
                           });
    
    const filtered = this.wizardItems
      .filter(item => {
        // Platform owner sees everything (bypasses RBAC checks)
        if (isPlatformOwner) {
          return true;
        }

        // Items from backend are already plan-filtered, so skip plan check here

        // Check visibility
        if (item.visible === false) {
          return false;
        }

        // TIER 2: Check RBAC permission requirement
        if (item.permission) {
          if (!this.auth.hasPermission()(item.permission)) {
            return false;
          }
        }

        // Check role requirement
        if (item.role) {
          const hasRole = userRoles.some(role => 
            role.toLowerCase() === item.role!.toLowerCase()
          );
          if (!hasRole) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    this.filteredWizardItems.set(filtered);
  }

  handleCommand(command: string): void {
    switch (command) {
      case 'refresh':
        window.location.reload();
        break;
      case 'toggle-theme':
        this.theme.toggle();
        break;
      case 'open-search':
        // Trigger command palette or search
        const event = new KeyboardEvent('keydown', {
          key: 'k',
          ctrlKey: true,
          bubbles: true
        });
        document.dispatchEvent(event);
        break;
      default:
        console.warn('Unknown command:', command);
    }
  }

  openModal(modalName: string): void {
    // Emit event or use a modal service
    const event = new CustomEvent('open-modal', { detail: { modal: modalName } });
    document.dispatchEvent(event);
  }
}

